'use client'

import { canEditClaim, getDisabledClaimTooltip } from '@repo/utils/claimPermissions'
import { convertDateToLocalTimeInIsoFormat, parseDate } from '@repo/utils/time'
import { DateSelection } from '@repo/ui/DateSelection'
import { MatterSelect } from '@repo/ui/MatterSelect'
import { OvertimeClaimTypeSelect } from '@repo/ui/OvertimeClaimTypeSelect'
import { TextEntry } from '@repo/ui/TextEntry'
import { ThirdPartyNotificationRecipientSelect } from '@repo/ui/ThirdPartyNotificationRecipientSelect'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useOvertimesStore } from '@repo/store/useOvertimesStore'
import { useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIDivider from '@mui/material/Divider'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

type OvertimeClaimType = {
  id?: string
  overtimeClaimTypeId?: string
  description: string
  label?: string
  value?: string
}

type FormData = {
  associatedMatter: any | null
  claimRecipientUserId: string | null
  dateIncurred: Date | null
  description: string
  overtimeClaimTypeId: string | null
  amount: number | null
  hours: number | null
}

type OvertimeClaimFormProps = {
  editingClaim?: { overtimeClaimId?: string } | null
  isSubmissionVersion?: boolean
  onSaveSuccess?: () => void
}

const sectionHeadingSx = {
  textTransform: 'uppercase',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: 'text.secondary',
  mt: 0,
  mb: 5
} as const

export const OvertimeClaimForm = ({
  editingClaim,
  isSubmissionVersion = false,
  onSaveSuccess
}: OvertimeClaimFormProps) => {
  const theme = useTheme()
  const { closeClaimsDrawer } = useClaimsStore()
  const { createOvertimeClaim, updateOvertimeClaim, fetchOvertimeDetails } = useOvertimesStore()

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [fetchedStatus, setFetchedStatus] = useState<number | null>(null)
  const [recipientOptions, setRecipientOptions] = useState<any[]>([])
  const [, setSelectedRecipient] = useState<any>(null)
  const [claimType, setClaimType] = useState<OvertimeClaimType | null>(null)

  const isEditingExistingClaim = !!editingClaim?.overtimeClaimId
  const isStatusEditDisabled = isEditingExistingClaim && !canEditClaim(isSubmissionVersion, fetchedStatus)

  const [formData, setFormData] = useState<FormData>({
    associatedMatter: null,
    claimRecipientUserId: null,
    dateIncurred: new Date(),
    description: '',
    overtimeClaimTypeId: null,
    amount: null,
    hours: null
  })

  useEffect(() => {
    if (!editingClaim?.overtimeClaimId) return

    const loadOvertimeDetails = async () => {
      setIsLoading(true)
      try {
        const response = await fetchOvertimeDetails({ overtimeClaimId: editingClaim.overtimeClaimId })

        if (response) {
          const claimTypeFromResponse = response.overtimeClaimTypeId
            ? {
                id: response.overtimeClaimTypeId,
                overtimeClaimTypeId: response.overtimeClaimTypeId,
                description: response.overtimeClaimTypeDescription || ''
              }
            : null
          setClaimType(claimTypeFromResponse)
          setFetchedStatus(response.status ?? 0)

          const associatedMatter =
            response.clientNo && response.matterNo
              ? {
                  clientNo: response.clientNo,
                  matterNo: response.matterNo,
                  clientMatterNo: `${response.clientNo}-${response.matterNo}`,
                  searchTerm: `${response.clientNo}-${response.matterNo}`
                }
              : null

          setFormData({
            associatedMatter,
            claimRecipientUserId: response.claimRecipientUserId || null,
            dateIncurred: response.incurredDate
              ? parseDate(String(response.incurredDate).slice(0, 10), 'yyyy-MM-dd')
              : new Date(),
            description: response.description || '',
            overtimeClaimTypeId: response.overtimeClaimTypeId || null,
            amount: response.amount || null,
            hours: response.hours || null
          })
        }
      } catch (error) {
        console.error('Error fetching overtime details:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadOvertimeDetails()
  }, [editingClaim?.overtimeClaimId])

  const updateFormField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[key]
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.dateIncurred) {
      newErrors.dateIncurred = 'Date incurred is required'
    }
    if (!formData.claimRecipientUserId) {
      newErrors.claimRecipientUserId = 'Claim recipient is required'
    }
    if (!claimType) {
      newErrors.claimType = 'Claim type is required'
    }
    if (!formData.amount) {
      newErrors.amount = 'Claim amount is required'
    }
    if (!formData.description.trim()) {
      newErrors.claimDescription = 'Claim description is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setIsSaving(true)

    const overtimePayload = {
      clientNo: formData.associatedMatter?.clientNo || null,
      claimRecipientUserId: formData.claimRecipientUserId,
      description: formData.description,
      overtimeClaimTypeId: formData.overtimeClaimTypeId,
      amount: formData.amount,
      hours: formData.hours,
      incurredDate: formData.dateIncurred
        ? convertDateToLocalTimeInIsoFormat(formData.dateIncurred).slice(0, 10)
        : null,
      matterNo: formData.associatedMatter?.matterNo || null,
      status: fetchedStatus ?? 0
    }

    try {
      if (isEditingExistingClaim) {
        await updateOvertimeClaim({
          ...overtimePayload,
          overtimeClaimId: editingClaim?.overtimeClaimId,
          isSubmitter: isSubmissionVersion
        })
      } else {
        await createOvertimeClaim(overtimePayload)
      }

      setIsSaving(false)
      if (onSaveSuccess) {
        onSaveSuccess()
      } else {
        closeClaimsDrawer()
      }
    } catch (error) {
      console.error('Error saving overtime claim:', error)
      setErrors({ submit: 'Failed to save overtime claim. Please try again.' })
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    closeClaimsDrawer()
  }

  if (isLoading) {
    return (
      <MUIBox
        data-testid="OvertimeClaimForm"
        sx={{
          mx: 4,
          mb: 4,
          pb: 2,
          backgroundColor: '#F4F0E7',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <MUITypography variant="body2" color="text.secondary">
          Loading claim details...
        </MUITypography>
      </MUIBox>
    )
  }

  return (
    <MUIBox
      data-testid="OvertimeClaimForm"
      sx={{
        mx: 4,
        mb: '1rem',
        pb: 2,
        mt: '1rem',
        backgroundColor: '#F4F0E7',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <MUIBox sx={{ flex: 1, px: 5, py: 5 }}>
        <MUITypography variant="h6" sx={sectionHeadingSx}>
          Claim Details
        </MUITypography>

        <MUIStack spacing={5}>
          <MUIStack direction="row" spacing={2}>
            <MUIBox sx={{ flex: 1 }}>
              <DateSelection
                format="d MMM yyyy"
                label="Date incurred"
                value={formData.dateIncurred}
                onChange={(date: any) => updateFormField('dateIncurred', date)}
                required
                name="dateIncurred"
                enableTypedDdMmYyyy={true}
                errors={errors}
                showAdornment={true}
              />
            </MUIBox>
            <MUIBox sx={{ flex: 1 }}>
              <ThirdPartyNotificationRecipientSelect
                label="Claim recipient"
                items={recipientOptions}
                setOptions={setRecipientOptions}
                setSelected={setSelectedRecipient}
                value={formData.claimRecipientUserId ?? ''}
                onChange={(value: any) => {
                  if (value) {
                    setSelectedRecipient(value)
                    updateFormField('claimRecipientUserId', value.value)
                  } else {
                    setSelectedRecipient(null)
                    updateFormField('claimRecipientUserId', null)
                  }
                }}
                required
              />
            </MUIBox>
          </MUIStack>

          <MatterSelect
            label="Associated matter"
            name="associatedMatter"
            value={formData.associatedMatter}
            onChange={(val: any) => updateFormField('associatedMatter', val)}
            errors={errors}
            menuZindex={999999}
          />

          <OvertimeClaimTypeSelect
            value={claimType}
            onChange={(val: any) => {
              setClaimType(val)
              setFormData((prev) => ({
                ...prev,
                overtimeClaimTypeId: val?.overtimeClaimTypeId || null
              }))
            }}
            required
            errors={errors}
          />
        </MUIStack>

        <MUIDivider sx={{ my: 5 }} />

        <MUITypography variant="h6" sx={sectionHeadingSx}>
          Claim Value
        </MUITypography>

        <MUIStack spacing={5}>
          <MUIStack direction="row" spacing={2}>
            <MUIBox sx={{ flex: 1 }}>
              <TextEntry
                label="Claim amount"
                value={formData.amount}
                onChange={(val: any) => updateFormField('amount', val)}
                isNumber
                decimalPlaces={2}
                name="amount"
                adornment="£"
                required
                errors={errors}
                sx={{ backgroundColor: 'white', minWidth: '120px', maxWidth: '220px' }}
              />
            </MUIBox>
            <MUIBox sx={{ flex: 1 }}>
              <TextEntry
                label="Overtime hours"
                value={formData.hours}
                onChange={(val: any) => updateFormField('hours', val)}
                isNumber
                decimalPlaces={2}
                name="hours"
                sx={{ backgroundColor: 'white', minWidth: '120px', maxWidth: '220px' }}
              />
            </MUIBox>
          </MUIStack>
        </MUIStack>

        <MUIDivider sx={{ my: 5 }} />

        <MUITypography variant="h6" sx={sectionHeadingSx}>
          Supporting Details
        </MUITypography>

        <MUIStack spacing={5}>
          <TextEntry
            label="Claim description"
            value={formData.description}
            onChange={(val: any) => updateFormField('description', val)}
            multiline
            rows={6}
            name="claimDescription"
            errors={errors}
            required
            sx={{ backgroundColor: 'white' }}
          />
        </MUIStack>

        <MUIStack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
          <MUIButton
            variant="text"
            onClick={handleCancel}
            sx={{
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            Cancel
          </MUIButton>
          <MUITooltip arrow placement="top" title={isStatusEditDisabled ? getDisabledClaimTooltip('edit') : ''}>
            <span>
              <MUIButton
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || isStatusEditDisabled}
                sx={{
                  textTransform: 'uppercase',
                  backgroundColor: theme.palette.success.light,
                  '&:hover': {
                    // @ts-ignore
                    backgroundColor: theme.palette.success.lightHover
                  },
                  '&:disabled': {
                    backgroundColor: '#B0BEC5'
                  }
                }}
              >
                {isSaving ? 'Saving...' : isEditingExistingClaim ? 'Save' : 'Save draft'}
              </MUIButton>
            </span>
          </MUITooltip>
        </MUIStack>
      </MUIBox>
    </MUIBox>
  )
}
